import { useEffect, useMemo, useState } from 'react'
import {
  CHESTNY_ZNAK_LABELS_RU,
  CHESTNY_ZNAK_VALUES,
  RAISED_CUSTOMS_FEE_RUB,
  formatDutyRate,
  type CustomsQueueRow,
} from '@crm/shared'
import { saveCustomsAnswer } from '../../../shared/api/customs.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { formatRuDate } from '../customsColDefs'
import { countUniqueParts } from '../customsSelection'
import {
  buildCustomsAnswer,
  dutyFractionFix,
  dutyFractionPercent,
  dutyInputText,
  genitivePositions,
  initialAnswerForm,
  parseDutyPercent,
  type AnswerFormState,
  type FeeChoice,
} from './customsAnswerForm'

/** Сервер принимает до 500 позиций за раз — больше отправляем порциями. */
const ANSWER_CHUNK = 500

const INPUT_CLS =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500'
const LABEL_CLS = 'block text-xs text-gray-500 mb-1'

const FEE_OPTIONS: ReadonlyArray<readonly [FeeChoice, string]> = [
  ['UNKNOWN', 'Неизвестно'],
  ['ZERO', '0 ₽'],
  ['RAISED', `Повышенный — ${RAISED_CUSTOMS_FEE_RUB.toLocaleString('ru-RU')} ₽`],
]

interface Props {
  rows: CustomsQueueRow[]
  onClose: () => void
  /** Итог записи — для тоста и сброса выделения. */
  onSaved: (result: { updated: number; skipped: number }) => void
}

/**
 * Ручной ввод ответа брокера (Л1): одна форма на все выбранные позиции — обычно
 * это один партномер из разных заявок. Пошлина вводится в процентах, на сервер
 * уходит долей (8 % → 0.08). Позиции получают статус «Ответ получен».
 */
export function CustomsAnswerModal({ rows, onClose, onSaved }: Props) {
  const init = useMemo(() => initialAnswerForm(rows), [rows])
  const [form, setForm] = useState<AnswerFormState>(init.form)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const set = (patch: Partial<AnswerFormState>) => setForm((f) => ({ ...f, ...patch }))

  const uniqueParts = useMemo(() => countUniqueParts(rows), [rows])
  // Ответ уже есть — новый его заменит: предупреждаем, особенно при нескольких позициях.
  const answeredCount = useMemo(() => rows.filter((r) => r.status === 'ANSWERED').length, [rows])

  // Что уйдёт на сервер из поля «Пошлина, %» и не доля ли это из файла брокера.
  const dutyRate = parseDutyPercent(form.duty)
  const dutyFraction = dutyFractionPercent(form.duty)

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose, saving])

  const submit = async () => {
    if (saving) return
    const answer = buildCustomsAnswer(form)
    if (typeof answer === 'string') {
      setError(answer)
      return
    }
    setError('')
    setSaving(true)
    const ids = rows.map((r) => r.itemId)
    const total = { updated: 0, skipped: 0 }
    try {
      for (let i = 0; i < ids.length; i += ANSWER_CHUNK) {
        const res = await saveCustomsAnswer({ itemIds: ids.slice(i, i + ANSWER_CHUNK), answer })
        total.updated += res.updated
        total.skipped += res.skipped
      }
      onSaved(total)
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось сохранить ответ'))
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-2xl mx-4 max-h-[92vh] flex flex-col">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">
            Ответ брокера{rows.length === 1 ? ` · ${rows[0]!.partNumber}` : ''}
          </h3>
          <button
            onClick={onClose}
            disabled={saving}
            aria-label="Закрыть"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none disabled:opacity-40"
          >
            ×
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <p className="text-xs text-gray-500">
            Позиций: <span className="font-medium text-gray-800">{rows.length}</span>
            {uniqueParts > 1 && (
              <span className="text-orange-700">
                {' '}
                · разных партномеров: {uniqueParts} — ответ запишется во все выбранные позиции
              </span>
            )}
          </p>
          {answeredCount > 0 && (
            <p className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
              У {genitivePositions(answeredCount)} уже есть ответ — он будет заменён.
            </p>
          )}
          {init.archiveDate && (
            <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              ТН ВЭД и «Честный знак» подставлены из архива от {formatRuDate(init.archiveDate)} —
              проверьте перед сохранением.
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLS} htmlFor="customs-tnved">
                Код ТН ВЭД (10 цифр)
              </label>
              <input
                id="customs-tnved"
                autoFocus
                inputMode="numeric"
                value={form.tnved}
                onChange={(e) => set({ tnved: e.target.value })}
                placeholder="8536690000"
                className={`${INPUT_CLS} font-mono`}
              />
            </div>
            <div>
              <label className={LABEL_CLS} htmlFor="customs-duty">
                Пошлина, %
              </label>
              <input
                id="customs-duty"
                inputMode="decimal"
                value={form.duty}
                onChange={(e) => set({ duty: e.target.value })}
                placeholder="например, 8 или 7,5"
                className={INPUT_CLS}
              />
              {typeof dutyRate === 'number' && (
                <p className="mt-1 text-xs text-gray-500" data-testid="customs-duty-preview">
                  = {formatDutyRate(dutyRate)}
                </p>
              )}
              {dutyFraction != null && (
                <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded-lg px-2 py-1.5">
                  <span>
                    Похоже на долю из файла брокера ({dutyInputText(form.duty)} ={' '}
                    {formatDutyRate(dutyFraction / 100)})
                  </span>
                  <button
                    type="button"
                    onClick={() => set({ duty: dutyFractionFix(form.duty) })}
                    className="px-2 py-0.5 border border-orange-300 rounded-md bg-white hover:bg-orange-100 text-orange-800"
                  >
                    Это {formatDutyRate(dutyFraction / 100)}
                  </button>
                </div>
              )}
            </div>
          </div>

          <fieldset className="space-y-1.5">
            <legend className={LABEL_CLS}>Таможенный сбор</legend>
            <div className="flex items-center gap-4 flex-wrap">
              {FEE_OPTIONS.map(([value, label]) => (
                <label
                  key={value}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-800"
                >
                  <input
                    type="radio"
                    name="customs-fee"
                    value={value}
                    checked={form.fee === value}
                    onChange={() => set({ fee: value })}
                    className="text-brand-600"
                  />
                  {label}
                </label>
              ))}
            </div>
            {form.fee === 'RAISED' && (
              <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                сумма, ₽
                <input
                  aria-label="Сумма повышенного сбора"
                  inputMode="decimal"
                  value={form.feeAmount}
                  onChange={(e) => set({ feeAmount: e.target.value })}
                  className="w-32 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </label>
            )}
          </fieldset>

          {(
            [
              ['nonTariffNote', 'Нетарифка (разрешительные документы)'],
              ['certificateNote', 'Сертификат'],
              ['featuresNote', 'Особенности'],
            ] as const
          ).map(([key, label]) => (
            <div key={key}>
              <label className={LABEL_CLS} htmlFor={`customs-${key}`}>
                {label}
              </label>
              <textarea
                id={`customs-${key}`}
                value={form[key]}
                maxLength={5000}
                rows={2}
                onChange={(e) => set({ [key]: e.target.value })}
                className={`${INPUT_CLS} resize-y`}
              />
            </div>
          ))}

          <div className="space-y-1.5">
            <div className={LABEL_CLS}>Честный знак</div>
            <div className="flex items-center gap-4 flex-wrap">
              {CHESTNY_ZNAK_VALUES.map((z) => (
                <label key={z} className="inline-flex items-center gap-1.5 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="customs-znak"
                    value={z}
                    checked={form.znak === z}
                    onChange={() => set({ znak: z })}
                    className="text-brand-600"
                  />
                  {CHESTNY_ZNAK_LABELS_RU[z]}
                </label>
              ))}
              {form.znak === 'YES' && (
                <label className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                  с
                  <input
                    type="date"
                    aria-label="Честный знак нужен с даты"
                    value={form.znakFrom}
                    onChange={(e) => set({ znakFrom: e.target.value })}
                    className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </label>
              )}
            </div>
          </div>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={() => void submit()}
            disabled={saving}
            className="px-4 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Сохранение…' : 'Сохранить ответ'}
          </button>
        </div>
      </div>
    </div>
  )
}
